/**
 * The Greasemap privacy policy, as structured content.
 *
 * Kept as data rather than markup so the page component stays presentational, and so the wording
 * can be reviewed as prose in one place. Every statement here was checked against the code: the
 * entity model in `server/src/Core`, the outbound calls in `server/src/Service`, and the client
 * storage in `apps/web/src/auth`. If the app starts collecting something new, this file changes in
 * the same commit.
 */

/** A block of content within a policy section. */
export type PolicyBlock =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'list'; readonly items: readonly string[] }
  | { readonly kind: 'table'; readonly head: readonly [string, string]; readonly rows: readonly (readonly [string, string])[] };

/** A titled section of the policy. */
export interface PolicySection {
  /** Stable id used for the in-page anchor and the contents list. */
  readonly id: string;
  /** The section heading. */
  readonly title: string;
  /** The section's content blocks, in order. */
  readonly blocks: readonly PolicyBlock[];
}

/** The date the current wording took effect, as an ISO date. */
export const PRIVACY_EFFECTIVE_DATE = '2026-09-24';

/** The address people can use to exercise their rights. */
export const PRIVACY_CONTACT_EMAIL = 'privacy@greasemap.app';

/** The natural person operating Greasemap. */
export const PRIVACY_OPERATOR = 'Anthony Sanfilippo';

export const privacyPolicy: readonly PolicySection[] = [
  {
    id: 'summary',
    title: 'The short version',
    blocks: [
      {
        kind: 'text',
        text: 'Greasemap is run by one person, not a data business. We keep what the app needs to work and nothing more.',
      },
      {
        kind: 'list',
        items: [
          'We never see or store your password. Microsoft handles sign-in.',
          'We do not store your email address.',
          'Every photo you upload has its EXIF metadata stripped before it is saved — including the GPS coordinates your phone writes into the file.',
          'Your VIN is only ever shown to you. Never to other users.',
          'We do not sell or share your personal information, and there are no advertising trackers on this site.',
          'You can have your account deleted at any time, and we mean deleted.',
        ],
      },
      {
        kind: 'text',
        text: 'The rest of this page is the detail behind those claims.',
      },
    ],
  },
  {
    id: 'who-we-are',
    title: 'Who we are',
    blocks: [
      {
        kind: 'text',
        text: `Greasemap is operated by ${PRIVACY_OPERATOR}, an individual, as the business responsible for the personal information described here. You can reach us at ${PRIVACY_CONTACT_EMAIL}.`,
      },
      {
        kind: 'text',
        text: 'This policy covers the Greasemap website at greasemap.app and the Greasemap mobile apps.',
      },
    ],
  },
  {
    id: 'what-we-collect',
    title: 'What we collect',
    blocks: [
      {
        kind: 'text',
        text: 'Almost everything Greasemap holds is something you typed in or uploaded on purpose. Here is the complete list.',
      },
      {
        kind: 'table',
        head: ['Category', 'What it is'],
        rows: [
          [
            'Account',
            'An identifier issued by Microsoft when you sign in, the display name shown to other users, an optional avatar image address, and whether your account is an enthusiast or an organizer account. We do not store your email address or your password.',
          ],
          [
            'Your garage',
            'The year, make, model and trim of each vehicle, an optional nickname, an optional VIN, the modifications you record with their optional cost and date, your wishlist, and the photos you upload.',
          ],
          [
            'Events',
            'Events you submit to the map, including the title, description, address, location and dates. Your RSVPs, and any confirmation you give that an event is still running.',
          ],
          [
            'Race hosting',
            'If you run events: your organizer name, your race events, the contestant list, brackets, and timing results. Timing results record the identifier of the device that captured them, so results from several scoring devices can be reconciled.',
          ],
          [
            'Technical',
            'Your IP address is used in memory to apply rate limits and is not written to our database. Our hosting provider records standard request logs and diagnostics.',
          ],
          [
            'Location',
            'If — and only if — you grant your browser or phone permission, we read your approximate location to centre the event map on you. It is used for that request and is not stored on our servers.',
          ],
        ],
      },
      {
        kind: 'text',
        text: 'We do not collect payment information, because Greasemap does not take payments. We do not use advertising cookies, pixels or third-party analytics trackers.',
      },
    ],
  },
  {
    id: 'photos',
    title: 'Photos and hidden metadata',
    blocks: [
      {
        kind: 'text',
        text: 'A photo taken on a phone usually carries EXIF metadata, and that metadata usually includes the exact GPS coordinates of where the picture was taken — which, for a photo of your car, is very often your home.',
      },
      {
        kind: 'text',
        text: 'Greasemap re-encodes every uploaded image on the server and discards all of that metadata before the image is stored. Rotation is applied first so the picture still appears the right way up, then the metadata is gone. The stored image and its thumbnail contain no EXIF, no GPS, no camera identifiers and no timestamps.',
      },
      {
        kind: 'text',
        text: 'We also determine the image type by decoding the file itself rather than trusting what your browser says it is.',
      },
      {
        kind: 'text',
        text: 'Stored photos are never publicly readable. They are served through short-lived links that expire after about an hour, generated only for people allowed to see that image.',
      },
    ],
  },
  {
    id: 'what-is-visible',
    title: 'What other people can see',
    blocks: [
      {
        kind: 'list',
        items: [
          'Your VIN is only ever returned to you. It is withheld from every other user, on every screen and in the API.',
          'A vehicle in your garage is private unless you mark it public.',
          'Your display name is visible where you take part publicly — as an RSVP on an event, or as an entrant in a race.',
          'Events you submit to the map are public, which is the point of submitting them. The map does not record or show who submitted an event.',
          'Modification costs and wishlists are private to you.',
        ],
      },
    ],
  },
  {
    id: 'sharing',
    title: 'Who else is involved',
    blocks: [
      {
        kind: 'text',
        text: 'We do not sell your personal information, and we do not share it for cross-context behavioural advertising. We use a small number of service providers to make the app work:',
      },
      {
        kind: 'table',
        head: ['Provider', 'What they receive, and why'],
        rows: [
          [
            'Microsoft Entra External ID',
            'Handles sign-in and holds your credentials. We receive only an identifier and a display name back.',
          ],
          [
            'Microsoft Azure',
            'Hosts the application, the database and your uploaded photos, and collects operational diagnostics such as request logs.',
          ],
          [
            'Microsoft Azure Maps',
            'Serves the map tiles on the website. Loading a map shares the area you are looking at with Microsoft.',
          ],
          [
            'Google Maps',
            'Serves the map in the Android app, on the same basis.',
          ],
          [
            'NHTSA vPIC',
            'A free public service run by the US Department of Transportation. If you enter a VIN to have it decoded, that VIN is sent to them to look up the vehicle specification. Nothing else about you is sent.',
          ],
        ],
      },
      {
        kind: 'text',
        text: 'We may also disclose information if the law requires it, or to investigate abuse of the service. If Greasemap is ever transferred to someone else, we will say so here before it happens.',
      },
    ],
  },
  {
    id: 'retention',
    title: 'How long we keep it, and deleting your account',
    blocks: [
      {
        kind: 'text',
        text: 'We keep what you put in for as long as your account exists. You can have your account deleted at any time by emailing us — we act on it immediately, not within some window. Deletion is permanent and there is no recovery period, so export your garage first if you want a copy.',
      },
      {
        kind: 'text',
        text: 'When you delete your account, the following is permanently removed:',
      },
      {
        kind: 'list',
        items: [
          'Your account record.',
          'Every vehicle in your garage, with its modifications, wishlist items, photos and the stored image files themselves.',
          'Your organizer profile and every race event you ran under it, including its brackets and timing results.',
          'Your RSVPs and your event confirmations.',
        ],
      },
      {
        kind: 'text',
        text: 'One thing is anonymised rather than deleted. If you competed in a race that somebody else organised, your entry stays in that event with your name and the link to your account and vehicle removed. Deleting the entry outright would break the bracket and the results for everyone else who raced that day. If you want that entry removed entirely as well, email us and we will ask the organiser.',
      },
      {
        kind: 'text',
        text: 'Events you submitted to the public map are not removed, because they are community information and the map never recorded that you were the one who submitted them.',
      },
      {
        kind: 'text',
        text: 'Backups and operational logs are kept for a short period by our hosting provider and age out on their own schedule.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    blocks: [
      {
        kind: 'text',
        text: 'Wherever you live, you can do all of the following. California residents have these rights under the CCPA, but we do not limit them by state.',
      },
      {
        kind: 'list',
        items: [
          'Know what we hold about you and why — this page is the answer, and you can ask for specifics.',
          'Get a copy. The garage export gives you your vehicles, modifications, wishlist and photos as a single download, at any time, without asking us.',
          'Correct anything that is wrong. Most of it you can edit directly in the app.',
          'Delete your account and everything attached to it. Email us and it is done.',
          'Not be treated differently for exercising any of these. Greasemap is free, and it stays free either way.',
        ],
      },
      {
        kind: 'text',
        text: `To make a request, email ${PRIVACY_CONTACT_EMAIL}. We will respond within 45 days. We have not sold or shared personal information for advertising in the preceding twelve months, and we do not intend to.`,
      },
    ],
  },
  {
    id: 'security',
    title: 'How we protect it',
    blocks: [
      {
        kind: 'list',
        items: [
          'All traffic to greasemap.app is encrypted with HTTPS and the site is served with a strict content security policy.',
          'Your password never reaches us. Sign-in is handled entirely by Microsoft.',
          'Uploaded photos are stored in private storage that is not publicly readable, and are served only through short-lived expiring links.',
          'The API applies rate limits to reduce abuse and automated scraping.',
          'Requests for data you do not own return "not found" rather than "forbidden", so the API does not confirm that another person\'s record exists.',
        ],
      },
      {
        kind: 'text',
        text: 'No service can promise perfect security, and we will not pretend otherwise. If a breach affects your information, we will tell you.',
      },
    ],
  },
  {
    id: 'children',
    title: 'Children',
    blocks: [
      {
        kind: 'text',
        text: 'Greasemap is not intended for children under 13, and we do not knowingly collect information from them. If you believe a child under 13 has created an account, email us and we will delete it.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    blocks: [
      {
        kind: 'text',
        text: 'If we change what we collect or who we share it with, we will update this page and change the effective date above. For anything significant, we will tell you in the app before it takes effect rather than quietly editing this page.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    blocks: [
      {
        kind: 'text',
        text: `Questions, requests or corrections: ${PRIVACY_CONTACT_EMAIL}. A real person reads it.`,
      },
    ],
  },
];
